/* =============================================================================
 * Boot do app.
 *
 * A ORDEM aqui e' obrigatoria e nao e' arbitraria:
 *
 *   config -> sessao restaurada -> OFFLINE -> renovacao de token -> SHELL -> UI
 *
 * O passo do shell e' bloqueante porque e' dele que vem o IdentificadorConexao
 * (o seletor de tenant), e esse valor entra no hash de toda URL de imagem. Se a
 * UI subisse antes, os escudos seriam gerados com hash errado e simplesmente
 * nao abririam - com 200 no console e nenhuma pista do motivo.
 *
 * O passo do offline vem ANTES dele pelo mesmo motivo: e' o indice em memoria
 * que permite responder o shell a partir do aparelho. Carregado depois, a
 * primeira pergunta do boot seria respondida com "nao tenho" e o app abriria
 * vazio - com o conteudo baixado intacto na base.
 * ========================================================================== */

import './estilos/tokens.css';
import './estilos/fontes.css';
import './estilos/base.css';

import { config } from '@shared/lib/config.js';
import { mensagemAmigavel, ErroRede, ErroOffline } from '@shared/lib/erros.js';
import { restaurar, aplicarShell, sessao } from '@shared/sessao/sessao.js';
import { instalarRenovacaoAutomatica } from '@shared/api/token.js';
import { iniciarOffline } from '@shared/offline/inicio.js';
import { iniciarInstalacao } from '@shared/instalacao/instalacao.js';
import { NOME_CACHE_IMAGENS } from '@shared/offline/armazenamento.js';
import { obterShellAnonimo, obterShellUsuario, carregarVinculosUsuario } from '@shared/sessao/autenticacao.js';
import { montarAppShell, aplicarTemaSalvo } from './shell/app-shell.js';
import { iniciar as iniciarRoteador } from './roteador.js';
import { instalarLinks } from './links.js';
import { instalarDicas } from '@shared/ui/dica.js';
import { h, render } from '@shared/ui/dom.js';
import { marcaFutigol } from '@shared/ui/marca.js';
import { icone } from '@shared/ui/componentes/icone.js';

async function iniciar() {
    const raiz = document.getElementById('app');

    aplicarTemaSalvo();
    restaurar();
    //ANTES de qualquer await: `beforeinstallprompt` dispara uma vez so' por
    //carga, e quem nao estiver ouvindo quando ele passar perde o convite de
    //instalar ate' a proxima visita. Nao vai a' rede - so' registra listeners.
    iniciarInstalacao();
    await iniciarOffline();
    instalarRenovacaoAutomatica();

    try {
        const shell = sessao.usuario ? await obterShellUsuario(sessao.usuario.IdUsuario) : await obterShellAnonimo();
        aplicarShell(shell);
    } catch (erro) {
        //Sem o shell nada funciona - entao aqui e' tela de erro com retry, nao
        //um app degradado que falha depois, de formas piores.
        //
        //A EXCECAO E' A FALTA DE REDE. Ai' a tela de erro com "tentar de novo" e'
        //um beco sem saida: tentar de novo vai falhar igual, e a pessoa fica sem
        //alcancar nem o conteudo que ja' baixou nem a tela que explica o offline.
        //O app sobe sem shell - a faixa da casca diz o que houve, os escudos
        //ficam sem imagem (o tenant vem do shell) e a saida e' /usuario/offline.
        if (!ehFalhaDeRede(erro)) {
            mostrarFalhaDeBoot(raiz, erro);
            return;
        }
        console.warn('[boot] sem conexão: subindo com o que existe no aparelho', erro);
    }

    //Precisa vir ANTES do roteador: os guards de /gestao consultam
    //capacidades(), que le os clubes vinculados. Se o roteador subisse antes,
    //quem abrisse /clubes/clube-administracao/5 direto seria redirecionado para a home por
    //uma corrida - com o vinculo chegando meio segundo depois.
    if (sessao.usuario) await carregarVinculosUsuario(sessao.usuario.IdUsuario);

    const saida = montarAppShell(raiz);
    instalarLinks();
    //Mesma natureza do instalarLinks: um listener no document, por delegacao,
    //que passa a valer para todo `title` que o app escrever de agora em diante.
    instalarDicas();
    iniciarRoteador(saida);

    registrarServiceWorker();
}

/**
 * Falta de rede, e nao erro de negocio.
 *
 * ErroOffline e' o app tendo recusado a chamada por ja' saber que nao ha' rede;
 * ErroRede com `semResposta` e' a requisicao que nao chegou ao servidor. Um HTTP
 * 500 nao entra aqui de proposito: ali o servidor respondeu, e subir um app sem
 * shell so' esconderia o problema.
 */
function ehFalhaDeRede(erro) {
    return erro instanceof ErroOffline || (erro instanceof ErroRede && erro.semResposta);
}

function mostrarFalhaDeBoot(raiz, erro) {
    console.error('[boot] falhou', erro);
    render(raiz, h('div', { class: 'pagina falha-boot' }, [h('div', { class: 'falha-boot__marca' }, marcaFutigol({ altura: 34 })), h('div', { class: 'estado__icone estado__icone--perigo' }, icone('alerta', { tamanho: 32 })), h('h1', { class: 'pagina__titulo' }, 'Não foi possível iniciar'), h('p', { class: 'pagina__subtitulo' }, mensagemAmigavel(erro)), h('p', { class: 'pagina__subtitulo' }, `Servidor: ${config.urlServico}`), h('button', { class: 'botao botao--principal', type: 'button', onclick: () => location.reload() }, 'Tentar de novo')]));
}

async function registrarServiceWorker() {
    if (!('serviceWorker' in navigator)) return;

    //Chave de emergencia herdada do iPrisma: se houver suspeita de conteudo
    //servido do cache, ligar a flag desregistra tudo na proxima carga.
    if (config.desabilitarServiceWorker) {
        const registros = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registros.map((r) => r.unregister()));
        if ('caches' in window) {
            const nomes = await caches.keys();
            //O cache de imagens do offline fica de fora: a chave de emergencia e'
            //sobre o service worker servindo asset velho, e apagar junto o que a
            //pessoa baixou de proposito seria destruir conteudo dela para
            //resolver um problema que nao e' esse. Ver shared/offline.
            await Promise.all(nomes.filter((n) => n.startsWith('futigol') && n !== NOME_CACHE_IMAGENS).map((n) => caches.delete(n)));
        }
        return;
    }

    if (import.meta.env.DEV) return;
    try {
        await navigator.serviceWorker.register('/sw.js', { type: 'module' });
    } catch (e) {
        console.warn('[sw] não registrado', e);
    }
}

if (config.modoDesenvolvedor) {
    //Facilita conferir chamadas no console durante o desenvolvimento.
    window.futigol = { config, sessao };
}

iniciar();
